/**
 * Kit domain helpers: labels, the category relevance rule, merging bills of
 * materials, and the client-side mirror of the kit stock validation rules.
 *
 * Pure functions with type-only imports, so nothing here touches the network or
 * the store.
 */

import type {
  KitKind,
  KitMovementKind,
  KitRequestStatus,
  KitRequirement,
  KitStockStatus,
  KitType,
  ServiceCategory,
  WarehouseMovementKind,
} from './types';

export const KIT_STATUS_LABELS: Record<KitStockStatus, string> = {
  ok: 'In stock',
  low: 'Low',
  out: 'Out',
};

export const KIT_MOVEMENT_LABELS: Record<KitMovementKind, string> = {
  issue: 'Issued',
  adjust: 'Adjusted',
  reserve: 'Reserved',
  release: 'Released',
  consume: 'Consumed',
  return: 'Returned',
};

export const KIT_MOVEMENT_KINDS: KitMovementKind[] = ['issue', 'return', 'adjust', 'reserve', 'release', 'consume'];

/** What each kind of item is, in the words the Production page uses. */
export const KIT_KIND_LABELS: Record<KitKind, string> = {
  kit: 'Kit',
  uniform: 'Uniform',
  equipment: 'Equipment',
};

export const KIT_KINDS: KitKind[] = ['kit', 'uniform', 'equipment'];

/** Reads a stored kind, treating anything unknown (or a legacy empty value) as a kit. */
export function kitKind(value: string | undefined | null): KitKind {
  return value === 'uniform' || value === 'equipment' ? value : 'kit';
}

export const WAREHOUSE_MOVEMENT_LABELS: Record<WarehouseMovementKind, string> = {
  receive: 'Received',
  adjust: 'Stocktake',
  issue: 'Issued',
  return: 'Returned',
};

export const WAREHOUSE_MOVEMENT_KINDS: WarehouseMovementKind[] = ['receive', 'issue', 'return', 'adjust'];

/** Server limits for warehouse operations (partner service). */
export const WAREHOUSE_LIMITS = {
  receiveQuantity: 100000,
  adjustDelta: 100000,
  reorderLevel: 100000,
  reference: 60,
} as const;

/** Partner IDs as the counter accepts them: "KC-10023", "kc 10023" or "10023". */
export function normalisePartnerCode(raw: string): string | null {
  const cleaned = raw.trim().toUpperCase().replace(/[\s_-]/g, '');
  const match = /^(?:KC)?(\d{4,9})$/.exec(cleaned);
  return match ? `KC-${Number(match[1])}` : null;
}

export const KIT_REQUEST_LABELS: Record<KitRequestStatus, string> = {
  pending: 'Pending',
  fulfilled: 'Fulfilled',
  rejected: 'Declined',
  cancelled: 'Cancelled',
};

/** Server limits for stock operations (partner service). */
export const KIT_LIMITS = {
  issueLines: 20,
  issueQuantity: 500,
  adjustDelta: 500,
  reason: { min: 5, max: 400 },
  note: 400,
} as const;

/** Server limits for the kit catalogue (catalog service). */
export const KIT_TYPE_LIMITS = {
  name: { min: 2, max: 120 },
  description: 500,
  unit: { min: 1, max: 20 },
  threshold: { min: 0, max: 1000 },
} as const;

/** SKU after upper-casing: 2 to 40 letters, digits or hyphens. */
export const KIT_SKU_PATTERN = /^[A-Z0-9-]{2,40}$/;

/**
 * `domain.StockStatus` on the server: out at zero or below, low at or below the
 * kit's threshold, ok above it. Used to preview the result of an adjustment.
 */
export function kitStockStatus(available: number, threshold: number): KitStockStatus {
  if (available <= 0) return 'out';
  if (available <= threshold) return 'low';
  return 'ok';
}

/** "2 × Hygiene kit, 1 × Hair care kit", for confirmation copy. */
export function kitLinesText(
  lines: { kitTypeId: string; name?: string; quantity: number }[],
  kitTypes: KitType[],
): string {
  return lines.map((line) => `${line.quantity} × ${kitName(line.kitTypeId, kitTypes, line.name)}`).join(', ');
}

/** True when a kit is relevant to a category: general kits are relevant to all. */
export function kitRelevantTo(kit: Pick<KitType, 'categoryId'>, categoryId: string): boolean {
  return kit.categoryId === '' || kit.categoryId === categoryId;
}

/**
 * Kits that can be picked for a package in `categoryId`: active, and general or
 * of that category. Kits already on the list are always kept, even when retired
 * or from another category, so a saved bill of materials never silently loses a
 * row in the picker — validation flags them instead.
 */
export function kitOptionsFor(
  kitTypes: KitType[],
  categoryId: string,
  keepIds: Iterable<string> = [],
): KitType[] {
  const keep = new Set(keepIds);
  return kitTypes
    .filter(
      (kit) =>
        keep.has(kit.id) ||
        // Only kits are used up on a job. Uniforms and equipment are issued to the
        // professional and kept, so the server refuses them in a bill of materials.
        (kit.active && kitKind(kit.kind) === 'kit' && kitRelevantTo(kit, categoryId)),
    )
    .sort(byPosition);
}

export function byPosition(a: KitType, b: KitType): number {
  return a.position - b.position || a.name.localeCompare(b.name);
}

/** "General" for a kit with no category, otherwise the category's name. */
export function kitCategoryLabel(
  categoryId: string,
  categories: Pick<ServiceCategory, 'id' | 'name'>[],
): string {
  if (!categoryId) return 'General';
  return categories.find((category) => category.id === categoryId)?.name ?? categoryId;
}

/** A kit's display name: the live kit type first, then the stored snapshot, then the id. */
export function kitName(kitTypeId: string, kitTypes: KitType[], snapshot?: string): string {
  return kitTypes.find((kit) => kit.id === kitTypeId)?.name || snapshot || kitTypeId;
}

/**
 * Merges bills of materials the way the server does at quote time: one row per
 * kit, quantities summed, in first-seen order.
 */
export function mergeKitRequirements(...lists: KitRequirement[][]): KitRequirement[] {
  const merged = new Map<string, KitRequirement>();
  for (const list of lists) {
    for (const item of list) {
      if (!item.kitTypeId || item.quantity <= 0) continue;
      const current = merged.get(item.kitTypeId);
      if (current) current.quantity += item.quantity;
      else merged.set(item.kitTypeId, { ...item });
    }
  }
  return [...merged.values()];
}

/** Scales a per-unit bill of materials. */
export function scaleKitRequirements(list: KitRequirement[], by: number): KitRequirement[] {
  return list.map((item) => ({ ...item, quantity: item.quantity * by }));
}

/** Characters as the server counts them (runes, after trimming). */
export function runeLength(value: string): number {
  return Array.from(value.trim()).length;
}

/** Validates a stock-operation reason (adjust, decline): 5 to 400 characters. */
export function reasonProblem(value: string): string | undefined {
  const length = runeLength(value);
  if (length === 0) return 'This is required.';
  if (length < KIT_LIMITS.reason.min) return `Must be at least ${KIT_LIMITS.reason.min} characters.`;
  if (length > KIT_LIMITS.reason.max) return `Must be ${KIT_LIMITS.reason.max} characters or fewer.`;
  return undefined;
}

/** Parses a whole number typed into a field. NaN for anything else. */
export function parseWhole(value: string): number {
  const trimmed = value.trim();
  return /^\d+$/.test(trimmed) ? Number(trimmed) : Number.NaN;
}

/** Parses a signed whole number, e.g. "-3" or "+2". NaN for anything else. */
export function parseSigned(value: string): number {
  const trimmed = value.trim().replace(/^\u2212/, '-');
  return /^[+-]?\d+$/.test(trimmed) ? Number(trimmed) : Number.NaN;
}
